import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  type DocumentData,
  type FirestoreDataConverter,
  type Unsubscribe,
  type QueryDocumentSnapshot,
  type SnapshotOptions,
} from "firebase/firestore";
import { firestore, USERS_COLLECTION } from "../lib/firebase";
import type {
  ExerciseRuntimeStatus,
  SessionFullProjection,
  SessionPublicDeck,
  SessionPublicExercise,
  SessionPublicQuestionnaire,
  SessionPublicProjection,
  SessionStatus,
  UserFormationIndex,
  UserSessionIndex,
} from "../types/access";
import { asRecord, toDate } from "../utils/firestoreData";

const SESSION_PUBLIC_COLLECTION = "sessionPublic";
const SESSION_FULL_COLLECTION = "sessionFull";
const SESSION_PUBLIC_DECKS_COLLECTION = "decks";
const SESSION_PUBLIC_EXERCISES_COLLECTION = "exercises";
const SESSION_PUBLIC_QUESTIONNAIRES_COLLECTION = "questionnaires";
const USER_SESSIONS_COLLECTION = "sessions";
const USER_FORMATIONS_COLLECTION = "formations";

function normalizeSessionStatus(value: unknown): SessionStatus {
  if (
    value === "draft" ||
    value === "planned" ||
    value === "active" ||
    value === "ended" ||
    value === "archived" ||
    value === "cancelled"
  ) {
    return value;
  }

  return "draft";
}

function normalizeUserSessionIndex(
  id: string,
  data: Record<string, unknown>,
): UserSessionIndex {
  return {
    participantSlotId:
      typeof data.participantSlotId === "string" ? data.participantSlotId : null,
    publicEndAt: toDate(data.publicEndAt),
    publicStartAt: toDate(data.publicStartAt),
    role:
      data.role === "participant" ||
      data.role === "trainer" ||
      data.role === "sessionAdmin" ||
      data.role === "author"
        ? data.role
        : "participant",
    sessionId: typeof data.sessionId === "string" ? data.sessionId : id,
    status: normalizeSessionStatus(data.status),
    title: typeof data.title === "string" ? data.title : "Session sans titre",
    updatedAt: toDate(data.updatedAt),
  };
}

function normalizeUserFormationIndex(
  id: string,
  data: Record<string, unknown>,
): UserFormationIndex {
  return {
    formationId: typeof data.formationId === "string" ? data.formationId : id,
    role: data.role === "author" ? "author" : "admin",
    title: typeof data.title === "string" ? data.title : "Formation sans titre",
    updatedAt: toDate(data.updatedAt),
  };
}

const userSessionConverter: FirestoreDataConverter<UserSessionIndex> = {
  fromFirestore(
    snapshot: QueryDocumentSnapshot<DocumentData>,
    options: SnapshotOptions,
  ): UserSessionIndex {
    return normalizeUserSessionIndex(
      snapshot.id,
      asRecord(snapshot.data(options)),
    );
  },
  toFirestore(value: UserSessionIndex): DocumentData {
    return value;
  },
};

const userFormationConverter: FirestoreDataConverter<UserFormationIndex> = {
  fromFirestore(
    snapshot: QueryDocumentSnapshot<DocumentData>,
    options: SnapshotOptions,
  ): UserFormationIndex {
    return normalizeUserFormationIndex(
      snapshot.id,
      asRecord(snapshot.data(options)),
    );
  },
  toFirestore(value: UserFormationIndex): DocumentData {
    return value;
  },
};

function normalizePublicProjection(
  id: string,
  data: Record<string, unknown>,
): SessionPublicProjection {
  return {
    contentTagId: typeof data.contentTagId === "string" ? data.contentTagId : null,
    currentHalfDay:
      typeof data.currentHalfDay === "string" ? data.currentHalfDay : null,
    id: typeof data.id === "string" ? data.id : id,
    liveSummary: typeof data.liveSummary === "string" ? data.liveSummary : "",
    publicEndAt: toDate(data.publicEndAt),
    publicStartAt: toDate(data.publicStartAt),
    status: normalizeSessionStatus(data.status),
    timezone: typeof data.timezone === "string" ? data.timezone : "Europe/Paris",
    title: typeof data.title === "string" ? data.title : "Session sans titre",
  };
}

function normalizeFullProjection(
  id: string,
  data: Record<string, unknown>,
): SessionFullProjection {
  return {
    contentTagId: typeof data.contentTagId === "string" ? data.contentTagId : null,
    fullMetadata: asRecord(data.fullMetadata),
    id: typeof data.id === "string" ? data.id : id,
    status: normalizeSessionStatus(data.status),
    title: typeof data.title === "string" ? data.title : "Session sans titre",
  };
}

function normalizePublicDeck(
  id: string,
  data: Record<string, unknown>,
): SessionPublicDeck {
  const pageCount = typeof data.pageCount === "number" ? data.pageCount : 0;
  const visibleMaxPage =
    typeof data.visibleMaxPage === "number" ? data.visibleMaxPage : 0;

  return {
    id: typeof data.id === "string" ? data.id : id,
    order: typeof data.order === "number" ? data.order : 0,
    pageCount: Math.max(0, Math.trunc(pageCount)),
    sessionId: typeof data.sessionId === "string" ? data.sessionId : "",
    sourceDeckId: typeof data.sourceDeckId === "string" ? data.sourceDeckId : "",
    title: typeof data.title === "string" ? data.title : "Support",
    versionId: typeof data.versionId === "string" ? data.versionId : "",
    visibleMaxPage: Math.max(0, Math.trunc(visibleMaxPage)),
  };
}

function normalizeQuestionnaireStatus(
  value: unknown,
): SessionPublicQuestionnaire["runtimeStatus"] {
  if (
    value === "open" ||
    value === "closed_to_new_entries" ||
    value === "closed" ||
    value === "correction_visible"
  ) {
    return value;
  }

  return "not_opened";
}

function normalizePublicQuestionnaire(
  id: string,
  data: Record<string, unknown>,
): SessionPublicQuestionnaire {
  return {
    closedAt: toDate(data.closedAt),
    closedToNewEntriesAt: toDate(data.closedToNewEntriesAt),
    correctionVisible: data.correctionVisible === true,
    correctionVisibleAt: toDate(data.correctionVisibleAt),
    id: typeof data.id === "string" ? data.id : id,
    openedAt: toDate(data.openedAt),
    order: typeof data.order === "number" ? data.order : 0,
    runtimeStatus: normalizeQuestionnaireStatus(data.runtimeStatus),
    sessionId: typeof data.sessionId === "string" ? data.sessionId : "",
    sourceQuestionnaireId:
      typeof data.sourceQuestionnaireId === "string"
        ? data.sourceQuestionnaireId
        : "",
    title: typeof data.title === "string" ? data.title : "Questionnaire",
    versionId: typeof data.versionId === "string" ? data.versionId : "",
    yamlDefinition:
      typeof data.yamlDefinition === "string" ? data.yamlDefinition : "",
  };
}

function normalizeExerciseStatus(value: unknown): ExerciseRuntimeStatus {
  if (
    value === "active" ||
    value === "passed" ||
    value === "correction_visible"
  ) {
    return value;
  }

  return "not_opened";
}

function normalizeExerciseResponseMode(
  value: unknown,
): SessionPublicExercise["responseMode"] {
  return value === "markdown" ? "markdown" : "none";
}

function normalizePublicExercise(
  id: string,
  data: Record<string, unknown>,
): SessionPublicExercise {
  return {
    activatedAt: toDate(data.activatedAt),
    correctionDeckFragment:
      typeof data.correctionDeckFragment === "string"
        ? data.correctionDeckFragment
        : "",
    correctionMarkdown:
      typeof data.correctionMarkdown === "string" ? data.correctionMarkdown : "",
    correctionVisible: data.correctionVisible === true,
    correctionVisibleAt: toDate(data.correctionVisibleAt),
    id: typeof data.id === "string" ? data.id : id,
    order: typeof data.order === "number" ? data.order : 0,
    passedAt: toDate(data.passedAt),
    responseMode: normalizeExerciseResponseMode(data.responseMode),
    runtimeStatus: normalizeExerciseStatus(data.runtimeStatus),
    sessionId: typeof data.sessionId === "string" ? data.sessionId : "",
    sourceExerciseId:
      typeof data.sourceExerciseId === "string" ? data.sourceExerciseId : "",
    statementMarkdown:
      typeof data.statementMarkdown === "string" ? data.statementMarkdown : "",
    title: typeof data.title === "string" ? data.title : "Exercice",
    versionId: typeof data.versionId === "string" ? data.versionId : "",
  };
}

export async function listCurrentUserSessions(
  uid: string,
): Promise<UserSessionIndex[]> {
  if (!firestore) {
    return [];
  }

  const sessionsQuery = query(
    collection(firestore, USERS_COLLECTION, uid, USER_SESSIONS_COLLECTION).withConverter(
      userSessionConverter,
    ),
    orderBy("updatedAt", "desc"),
  );
  const snapshot = await getDocs(sessionsQuery);
  return snapshot.docs.map((sessionDoc) => sessionDoc.data());
}

export async function listCurrentUserFormations(
  uid: string,
): Promise<UserFormationIndex[]> {
  if (!firestore) {
    return [];
  }

  const formationsQuery = query(
    collection(
      firestore,
      USERS_COLLECTION,
      uid,
      USER_FORMATIONS_COLLECTION,
    ).withConverter(userFormationConverter),
    orderBy("updatedAt", "desc"),
  );
  const snapshot = await getDocs(formationsQuery);
  return snapshot.docs.map((formationDoc) => formationDoc.data());
}

export async function readSessionPublicProjection(
  sessionId: string,
): Promise<SessionPublicProjection | null> {
  if (!firestore) {
    return null;
  }

  const snapshot = await getDoc(doc(firestore, SESSION_PUBLIC_COLLECTION, sessionId));

  if (!snapshot.exists()) {
    return null;
  }

  return normalizePublicProjection(snapshot.id, asRecord(snapshot.data()));
}

export async function readSessionFullProjection(
  sessionId: string,
): Promise<SessionFullProjection | null> {
  if (!firestore) {
    return null;
  }

  const snapshot = await getDoc(doc(firestore, SESSION_FULL_COLLECTION, sessionId));

  if (!snapshot.exists()) {
    return null;
  }

  return normalizeFullProjection(snapshot.id, asRecord(snapshot.data()));
}

export async function listSessionPublicDecks(
  sessionId: string,
): Promise<SessionPublicDeck[]> {
  if (!firestore) {
    return [];
  }

  const decksQuery = query(
    collection(
      firestore,
      SESSION_PUBLIC_COLLECTION,
      sessionId,
      SESSION_PUBLIC_DECKS_COLLECTION,
    ),
    orderBy("order", "asc"),
  );
  const snapshot = await getDocs(decksQuery);

  return snapshot.docs.map((deckDoc) =>
    normalizePublicDeck(deckDoc.id, asRecord(deckDoc.data())),
  );
}

export function subscribeSessionPublicDecks({
  onError,
  onNext,
  sessionId,
}: {
  onError: (error: Error) => void;
  onNext: (decks: SessionPublicDeck[]) => void;
  sessionId: string;
}): Unsubscribe {
  if (!firestore) {
    onNext([]);
    return () => undefined;
  }

  const decksQuery = query(
    collection(
      firestore,
      SESSION_PUBLIC_COLLECTION,
      sessionId,
      SESSION_PUBLIC_DECKS_COLLECTION,
    ),
    orderBy("order", "asc"),
  );

  return onSnapshot(
    decksQuery,
    (snapshot) => {
      onNext(
        snapshot.docs.map((deckDoc) =>
          normalizePublicDeck(deckDoc.id, asRecord(deckDoc.data())),
        ),
      );
    },
    onError,
  );
}

export async function listSessionPublicQuestionnaires(
  sessionId: string,
): Promise<SessionPublicQuestionnaire[]> {
  if (!firestore) {
    return [];
  }

  const questionnairesQuery = query(
    collection(
      firestore,
      SESSION_PUBLIC_COLLECTION,
      sessionId,
      SESSION_PUBLIC_QUESTIONNAIRES_COLLECTION,
    ),
    orderBy("order", "asc"),
  );
  const snapshot = await getDocs(questionnairesQuery);

  return snapshot.docs.map((questionnaireDoc) =>
    normalizePublicQuestionnaire(
      questionnaireDoc.id,
      asRecord(questionnaireDoc.data()),
    ),
  );
}

export function subscribeSessionPublicQuestionnaires({
  onError,
  onNext,
  sessionId,
}: {
  onError: (error: Error) => void;
  onNext: (questionnaires: SessionPublicQuestionnaire[]) => void;
  sessionId: string;
}): Unsubscribe {
  if (!firestore) {
    onNext([]);
    return () => undefined;
  }

  const questionnairesQuery = query(
    collection(
      firestore,
      SESSION_PUBLIC_COLLECTION,
      sessionId,
      SESSION_PUBLIC_QUESTIONNAIRES_COLLECTION,
    ),
    orderBy("order", "asc"),
  );

  return onSnapshot(
    questionnairesQuery,
    (snapshot) => {
      onNext(
        snapshot.docs.map((questionnaireDoc) =>
          normalizePublicQuestionnaire(
            questionnaireDoc.id,
            asRecord(questionnaireDoc.data()),
          ),
        ),
      );
    },
    onError,
  );
}

export async function listSessionPublicExercises(
  sessionId: string,
): Promise<SessionPublicExercise[]> {
  if (!firestore) {
    return [];
  }

  const exercisesQuery = query(
    collection(
      firestore,
      SESSION_PUBLIC_COLLECTION,
      sessionId,
      SESSION_PUBLIC_EXERCISES_COLLECTION,
    ),
    orderBy("order", "asc"),
  );
  const snapshot = await getDocs(exercisesQuery);

  return snapshot.docs.map((exerciseDoc) =>
    normalizePublicExercise(exerciseDoc.id, asRecord(exerciseDoc.data())),
  );
}

export function subscribeSessionPublicExercises({
  onError,
  onNext,
  sessionId,
}: {
  onError: (error: Error) => void;
  onNext: (exercises: SessionPublicExercise[]) => void;
  sessionId: string;
}): Unsubscribe {
  if (!firestore) {
    onNext([]);
    return () => undefined;
  }

  const exercisesQuery = query(
    collection(
      firestore,
      SESSION_PUBLIC_COLLECTION,
      sessionId,
      SESSION_PUBLIC_EXERCISES_COLLECTION,
    ),
    orderBy("order", "asc"),
  );

  return onSnapshot(
    exercisesQuery,
    (snapshot) => {
      onNext(
        snapshot.docs.map((exerciseDoc) =>
          normalizePublicExercise(exerciseDoc.id, asRecord(exerciseDoc.data())),
        ),
      );
    },
    onError,
  );
}
